import React from 'react';
import { MapPin } from 'lucide-react';
import {
  dutyDurationLabel,
  dutyStatusMeta,
  mobilePresenceMeta,
  readinessLocation,
  readinessStatusMeta,
} from './personnelAvailability';

const LIGHT_TONE_CLASSES = {
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  danger: 'border-rose-200 bg-rose-50 text-rose-700',
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  muted: 'border-slate-200 bg-slate-50 text-slate-600',
};

const DARK_TONE_CLASSES = {
  ready: 'border-emerald-800 bg-emerald-950/70 text-emerald-300',
  danger: 'border-rose-800 bg-rose-950/70 text-rose-300',
  pending: 'border-amber-800 bg-amber-950/70 text-amber-300',
  muted: 'border-slate-700 bg-slate-800 text-slate-300',
};

const PRESENCE_CLASSES = {
  light: {
    online: 'text-cyan-700',
    offline: 'text-amber-700',
    muted: 'text-slate-500',
  },
  dark: {
    online: 'text-cyan-300',
    offline: 'text-amber-300',
    muted: 'text-slate-400',
  },
};

const PRESENCE_DOT_CLASSES = {
  online: 'bg-cyan-500 shadow-[0_0_5px_rgba(6,182,212,0.75)]',
  offline: 'bg-amber-500',
  muted: 'bg-slate-400',
};

const StatusBadge = ({ prefix, meta, dark, compact, testId }) => {
  const tones = dark ? DARK_TONE_CLASSES : LIGHT_TONE_CLASSES;
  return (
    <span
      className={`rounded-full border font-bold ${compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]'} ${tones[meta.tone] || tones.muted}`}
      title={prefix === 'Duty' ? 'General duty status' : 'Readiness for this incident'}
      aria-label={`${prefix}: ${meta.label}`}
      data-testid={testId}
    >
      {meta.label}
    </span>
  );
};

export const PersonnelAvailabilitySummary = ({
  presence,
  dutyInfo,
  readinessInfo,
  readinessLoadState = 'loaded',
  variant = 'light',
  align = 'end',
  compact = true,
  nowMs = Date.now(),
  testId,
  className = '',
  showPresence = true,
  showDuty = true,
  showIncident = readinessInfo !== undefined,
  showLocation = readinessInfo !== undefined,
}) => {
  const dark = variant === 'dark';
  const startAligned = align === 'start';
  const presenceMeta = mobilePresenceMeta(presence, nowMs);
  const dutyMeta = dutyStatusMeta(dutyInfo);
  const readinessMeta = readinessStatusMeta(readinessInfo, readinessLoadState);
  const location = readinessLocation(readinessInfo, nowMs);
  const dutyAge = dutyDurationLabel(dutyInfo, nowMs);
  const presenceTone = presenceMeta.tone === 'online'
    ? 'online'
    : presenceMeta.tone === 'offline'
      ? 'offline'
      : 'muted';

  return (
    <span
      className={`flex min-w-0 shrink-0 flex-col ${compact ? 'gap-1' : 'gap-1.5'} ${startAligned ? 'items-start' : 'items-end'} ${className}`}
      data-testid={testId}
    >
      <span className={`inline-flex flex-wrap items-center gap-1 ${startAligned ? 'justify-start' : 'justify-end'}`}>
        {showPresence && (
          <span
            className={`inline-flex items-center gap-1 font-bold ${compact ? 'text-[9px]' : 'text-[10px]'} ${PRESENCE_CLASSES[dark ? 'dark' : 'light'][presenceTone]}`}
            title="Mobile app presence"
            data-testid={testId ? `${testId}-presence` : undefined}
          >
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${PRESENCE_DOT_CLASSES[presenceTone]}`} aria-hidden="true" />
            {presenceMeta.label}
          </span>
        )}
        {showDuty && (
          <span title={dutyAge ? `Latest general on-duty confirmation was ${dutyAge} ago` : undefined}>
            <StatusBadge
              prefix="Duty"
              meta={dutyMeta}
              dark={dark}
              compact={compact}
              testId={testId ? `${testId}-duty` : undefined}
            />
          </span>
        )}
        {showIncident && (
          <StatusBadge
            prefix="Incident"
            meta={readinessMeta}
            dark={dark}
            compact={compact}
            testId={testId ? `${testId}-incident` : undefined}
          />
        )}
      </span>
      {showLocation && (
        <span
          className={`inline-flex items-center gap-1 font-semibold ${compact ? 'text-[9px]' : 'text-[10px]'} ${location.stale
            ? 'text-amber-500'
            : dark
              ? 'text-cyan-400'
              : 'text-cyan-700'}`}
          title={location.stale ? 'Stale GPS is ignored for ETA and ranking' : 'Latest incident-readiness location'}
          data-testid={testId ? `${testId}-location` : undefined}
        >
          <MapPin className={compact ? 'h-2.5 w-2.5 shrink-0' : 'h-3 w-3 shrink-0'} aria-hidden="true" />
          {location.text}
        </span>
      )}
    </span>
  );
};

export default PersonnelAvailabilitySummary;
